import React, { useEffect, useState, useMemo } from 'react'
import { FaInbox } from 'react-icons/fa'
import OutletSearch from '../../SelectSearch/OutletSearch'
import Navbar from '../../Navbar'
import Footer from '../../Footer'
import axios from 'axios'
import * as XLSX from 'xlsx'
import { formatSalesValue } from '../../../utils/formatUtils'
import { useNavigate } from 'react-router-dom'
import PageStatus from '../../PageStatus/PageStatus'
import { toast } from 'react-toastify'
import { AggregatedCostCenterTable, NormalCostCenterTable } from './WastageReportTables'
import { formatToISODate } from '../../../utils/formateDateUtils'

// ─── helpers ──────────────────────────────────────────────────────────────────

const fmtDate = (raw) =>
    new Date(raw).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    });

const normKey = (raw) => (raw || '').toString().trim().replace(/\s+/g, ' ').toLowerCase();
const cleanDisplay = (raw) => (raw || '').toString().trim().replace(/\s+/g, ' ');

const groupNormal = (data) => {
    const grouped = data.reduce((acc, item) => {
        const cc = cleanDisplay(item.costcenter) || 'Unknown';
        const date = fmtDate(item.received_date);
        if (!acc[cc]) acc[cc] = {};
        if (!acc[cc][date]) acc[cc][date] = [];
        acc[cc][date].push(item);
        return acc;
    }, {});

    Object.values(grouped).forEach((dateGroups) => {
        Object.values(dateGroups).forEach((items) => {
            items.sort((a, b) => parseFloat(b.price || 0) - parseFloat(a.price || 0));
        });
    });

    return grouped;
};

const groupAggregated = (data) => {
    const intermediate = data.reduce((acc, item) => {
        const cc = cleanDisplay(item.costcenter) || 'Unknown';
        const key = `${normKey(item.item_name)}||${normKey(item.uom)}`;
        if (!acc[cc]) acc[cc] = {};
        if (!acc[cc][key]) {
            acc[cc][key] = {
                name: cleanDisplay(item.item_name),
                uom: cleanDisplay(item.uom),
                totalQty: 0,
                totalAmount: 0,
                rateSum: 0,
                rateCount: 0,
            };
        }
        const qty = parseFloat(item.quantity || 0);
        const amount = parseFloat(item.price || 0);
        const rate = qty > 0 ? amount / qty : 0;

        acc[cc][key].totalQty += qty;
        acc[cc][key].totalAmount += amount;
        acc[cc][key].rateSum += rate;
        acc[cc][key].rateCount += 1;
        return acc;
    }, {});

    return Object.fromEntries(
        Object.entries(intermediate).map(([cc, items]) => [
            cc,
            Object.values(items)
                .map((i) => ({
                    name: i.name,
                    uom: i.uom,
                    totalQty: i.totalQty,
                    totalAmount: i.totalAmount,
                    avgRate: i.rateCount > 0 ? i.rateSum / i.rateCount : 0,
                }))
                .sort((a, b) => b.totalAmount - a.totalAmount),
        ])
    );
};

// ─── sub-components ───────────────────────────────────────────────────────────

export const thClass = 'px-3 py-2.5 font-semibold text-gray-700 whitespace-nowrap tracking-wider uppercase text-xs';
export const tdClass = 'px-3 py-2 text-gray-700';

// ─── main component ───────────────────────────────────────────────────────────

const WastageReport = () => {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const today = new Date().toLocaleDateString('en-CA');
    const token = localStorage.getItem('token');
    const navigate = useNavigate();

    const [selectedOutlet, setSelectedOutlet] = useState();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [startDate, setStartDate] = useState(today);
    const [endDate, setEndDate] = useState(today);
    const [tab, setTab] = useState('normal');

    const fetchData = async (start, end) => {
        if (!selectedOutlet) return setError('Please select outlet');
        try {
            setLoading(true);
            setError(null);
            const res = await axios.post(`${baseUrl}/get-wastage-items`, {
                token,
                outlet_name: selectedOutlet,
                from_date: start,
                to_date: end,
            });
            setData(res.data?.data || []);
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.message || err.response?.data?.error || 'Failed to load wastage report');
        } finally {
            setLoading(false);
        }
    };

    const fetchDates = async () => {
        if (!token || !selectedOutlet) return;
        setStartDate(null);

        try {
            const foodResponse = await axios.post(`${baseUrl}/get-inventory-period-food`, {
                token: token,
                outlet: selectedOutlet
            });

            const foodData = foodResponse.data;
            const foodIsoStart = formatToISODate(foodData?.opening_date);
            setStartDate(foodIsoStart);
            fetchData(foodIsoStart, endDate)

        } catch (err) {
            toast.error("Failed to fetch dates");
        }
    };

    useEffect(() => {
        fetchDates();
    }, [token, selectedOutlet]);

    const normalGroups = useMemo(() => groupNormal(data), [data]);
    const aggregatedGroups = useMemo(() => groupAggregated(data), [data]);

    const summaryData = useMemo(() =>
        Object.values(
            data.reduce((acc, item) => {
                const cc = cleanDisplay(item.costcenter) || 'Unknown';
                if (!acc[cc]) acc[cc] = { costcenter: cc, uniqueItems: new Set(), quantity: 0, amount: 0 };
                acc[cc].uniqueItems.add(`${normKey(item.item_name)}||${normKey(item.uom)}`);
                acc[cc].quantity += parseFloat(item.quantity || 0);
                acc[cc].amount += parseFloat(item.price || 0);
                return acc;
            }, {})
        ).map((i) => ({ ...i, uniqueItems: i.uniqueItems.size })),
        [data]
    );

    const handleDeleteItem = async (item) => {
        toast.info(
            <div className="p-2">
                <p className="mb-1 text-white">
                    Are you sure you want to delete <strong>"{item.item_name}"</strong>?
                </p>

                <p className="mb-3 text-white">
                    Quantity: <strong>{item.quantity} {item.uom}</strong>
                </p>

                <div className="flex justify-between gap-2">
                    <button
                        className="px-4 py-2 bg-danger text-white rounded hover-bg-danger"
                        onClick={async () => {
                            toast.dismiss();
                            try {
                                await axios.post(`${baseUrl}/delete-wastage-items`, {
                                    token,
                                    id: item.id
                                });
                                fetchData(startDate, endDate);
                                toast.success("Item deleted successfully");
                            } catch (error) {
                                console.error('Error deleting unit definition:', error);
                                toast.error(
                                    error?.response?.data?.error ||
                                    error?.response?.data?.message ||
                                    'Error deleting unit definition'
                                );
                            }
                        }}
                    >
                        Delete
                    </button>

                    <button
                        className="px-4 py-2 bg-secondary text-white rounded hover-bg-secondary"
                        onClick={() => toast.dismiss()}
                    >
                        Cancel
                    </button>
                </div>
            </div>,
            {
                autoClose: false,
                closeButton: false,
                toastId: "delete-confirmation",
            }
        );
    };

    // ── excel export ────────────────────────────────────────────────────────
    const exportToExcel = () => {
        if (data.length === 0) return;

        const outletLabel = selectedOutlet || '';
        const workbook = XLSX.utils.book_new();

        if (tab === 'normal') {
            // One sheet per cost center; rows grouped by date with subtotals
            Object.entries(normalGroups).forEach(([cc, dateGroups]) => {
                const sheetData = [
                    ['WASTAGE REPORT — DETAILED'],
                    [],
                    ['Outlet', outletLabel],
                    ['Cost Center', cc],
                    ['From Date', startDate],
                    ['To Date', endDate],
                    [],
                    ['Item', 'UOM', 'Rate', 'Quantity', 'Amount'],
                ];

                let ccQty = 0;
                let ccAmt = 0;

                Object.entries(dateGroups).forEach(([date, items]) => {
                    // Date header row (spans visually via empty cells)
                    sheetData.push([date, '', '', '', '']);

                    let dateQty = 0;
                    let dateAmt = 0;

                    items.forEach((item) => {
                        const qty = parseFloat(item.quantity || 0);
                        const price = parseFloat(item.price || 0);
                        const rate = qty > 0 ? parseFloat((price / qty).toFixed(4)) : 0;
                        sheetData.push([item.item_name, item.uom, rate, qty, price]);
                        dateQty += qty;
                        dateAmt += price;
                    });

                    // Date subtotal
                    sheetData.push(['', '', `${date} subtotal`, dateQty, dateAmt]);
                    sheetData.push([]);

                    ccQty += dateQty;
                    ccAmt += dateAmt;
                });

                // Cost-center total
                sheetData.push(['', '', `${cc} Total`, ccQty, ccAmt]);

                const ws = XLSX.utils.aoa_to_sheet(sheetData);
                ws['!cols'] = [{ wch: 40 }, { wch: 12 }, { wch: 18 }, { wch: 14 }, { wch: 15 }];

                const sheetName = cc.replace(/[\\/*?[\]:]/g, '').slice(0, 31);
                XLSX.utils.book_append_sheet(workbook, ws, sheetName);
            });

            XLSX.writeFile(workbook, `Wastage_Detailed_${startDate}_to_${endDate}.xlsx`);

        } else {
            // One sheet per cost center; one row per unique item
            Object.entries(aggregatedGroups).forEach(([cc, items]) => {
                const sheetData = [
                    ['WASTAGE REPORT — AGGREGATED'],
                    [],
                    ['Outlet', outletLabel],
                    ['Cost Center', cc],
                    ['From Date', startDate],
                    ['To Date', endDate],
                    [],
                    ['Item', 'UOM', 'Avg Rate', 'Total Qty', 'Total Amount'],
                ];

                let totalQty = 0;
                let totalAmt = 0;

                items.forEach((item) => {
                    sheetData.push([
                        item.name,
                        item.uom,
                        parseFloat(item.avgRate.toFixed(4)),
                        parseFloat(item.totalQty.toFixed(2)),
                        parseFloat(item.totalAmount.toFixed(2)),
                    ]);
                    totalQty += item.totalQty;
                    totalAmt += item.totalAmount;
                });

                sheetData.push([]);
                sheetData.push(['', '', `${cc} Total`, parseFloat(totalQty.toFixed(2)), parseFloat(totalAmt.toFixed(2))]);

                const ws = XLSX.utils.aoa_to_sheet(sheetData);
                ws['!cols'] = [{ wch: 40 }, { wch: 12 }, { wch: 14 }, { wch: 14 }, { wch: 15 }];

                const sheetName = cc.replace(/[\\/*?[\]:]/g, '').slice(0, 31);
                XLSX.utils.book_append_sheet(workbook, ws, sheetName);
            });

            XLSX.writeFile(workbook, `Wastage_Aggregated_${startDate}_to_${endDate}.xlsx`);
        }
    };

    const hasData = !loading && !error && data.length > 0;

    return (
        <div className="flex flex-col min-h-screen">
            <Navbar />

            <div className="flex-grow p-3 max-w-5xl mx-auto w-full">

                <div className="flex justify-center items-center mb-3">
                    <div className="flex-1 flex justify-start gap-3 items-center">
                        <label className="font-base font-poppins md:text-base">From:</label>
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="text-xs md:text-base cursor-pointer border rounded px-3 py-2"
                        />
                    </div>

                    <div className="flex-1 flex justify-center">
                        <OutletSearch
                            selectedOutlet={selectedOutlet}
                            setSelectedOutlet={setSelectedOutlet}
                            token={token}
                        />
                    </div>

                    <div className="flex-1 flex items-center justify-end gap-3">
                        <label className="font-base font-poppins md:text-base">To:</label>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="text-xs md:text-base cursor-pointer border rounded px-3 py-2"
                        />
                    </div>
                </div>

                <div className="flex justify-center items-center mb-4">
                    <div className="flex-1 flex justify-start">
                        <button
                            className="px-4 py-2 rounded-md font-medium bg-sky-100 text-sky-800 hover:bg-sky-200 hover:shadow-md text-sm transition"
                            onClick={() => navigate('/add-wastage')}
                        >
                            Add Wastage
                        </button>
                    </div>

                    <div className="flex-1 flex justify-center">
                        <button
                            onClick={() => fetchData(startDate, endDate)}
                            disabled={loading}
                            className="px-4 py-2 rounded-md font-medium bg-purple-100 text-purple-800 hover:bg-purple-200 hover:shadow-md text-sm transition"
                        >
                            {loading ? 'Loading...' : 'View'}
                        </button>
                    </div>

                    <div className="flex-1 flex justify-end">
                        <button
                            onClick={exportToExcel}
                            disabled={data.length === 0}
                            className="px-4 py-2 rounded-md font-medium bg-green-100 text-green-800 hover:bg-green-200 hover:shadow-md text-sm transition disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Export to Excel
                        </button>
                    </div>
                </div>

                <div className="bg-white rounded-md shadow-md overflow-hidden">
                    <PageStatus loading={loading} error={error} loadingText="Loading wastage data..." />
                </div>

                {hasData && (
                    <div className="mb-4 bg-white rounded-md shadow-md overflow-hidden">
                        <div className="px-4 py-2 border-b bg-gray-50">
                            <div className="font-semibold text-gray-700 text-base">Cost Center Summary</div>
                        </div>

                        <div className="overflow-auto tiny-scrollbar">
                            <table className="w-full text-sm text-center">
                                <thead className="bg-gray-100">
                                    <tr>
                                        <th className={thClass}>Cost Center</th>
                                        <th className={thClass}>Unique Items</th>
                                        <th className={thClass}>Total Quantity</th>
                                        <th className={thClass}>Total Amount</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y">
                                    {summaryData.map((item, index) => (
                                        <tr key={index} className="hover:bg-gray-50 transition">
                                            <td className={tdClass}>{item.costcenter}</td>
                                            <td className={`${tdClass} font-medium`}>{item.uniqueItems}</td>
                                            <td className={tdClass}>{item.quantity.toFixed(2)}</td>
                                            <td className={`${tdClass} font-semibold text-red-600`}>{formatSalesValue(item.amount)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot className="bg-gray-50">
                                    <tr>
                                        <td className="px-3 py-2 font-semibold text-gray-700">Grand Total</td>
                                        <td className="px-3 py-2 font-semibold text-gray-700">
                                            {summaryData.reduce((s, i) => s + i.uniqueItems, 0)}
                                        </td>
                                        <td className="px-3 py-2 font-semibold text-gray-700">
                                            {summaryData.reduce((s, i) => s + i.quantity, 0).toFixed(2)}
                                        </td>
                                        <td className="px-3 py-2 font-semibold text-red-600">
                                            {formatSalesValue(summaryData.reduce((s, i) => s + i.amount, 0))}
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                )}

                {!loading && !error && data.length === 0 && (
                    <div className="flex flex-col items-center justify-center h-40 text-gray-500">
                        <FaInbox className="text-2xl mb-2" />
                        <p>Wastage data not found</p>
                    </div>
                )}

                {hasData && (
                    <>
                        <div className="flex border-b border-gray-200 mb-3">
                            <button
                                onClick={() => setTab('normal')}
                                className={`px-5 py-2 text-sm font-medium transition border-b-2 -mb-px ${tab === 'normal'
                                    ? 'border-purple-600 text-purple-700'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                                    }`}
                            >
                                Detailed
                            </button>
                            <button
                                onClick={() => setTab('aggregated')}
                                className={`px-5 py-2 text-sm font-medium transition border-b-2 -mb-px ${tab === 'aggregated'
                                    ? 'border-purple-600 text-purple-700'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                                    }`}
                            >
                                Aggregated
                            </button>
                        </div>

                        {tab === 'normal' &&
                            Object.entries(normalGroups).map(([cc, dateGroups]) => (
                                <NormalCostCenterTable
                                    key={cc}
                                    costcenter={cc}
                                    dateGroups={dateGroups}
                                    handleDelete={handleDeleteItem}
                                />
                            ))
                        }

                        {tab === 'aggregated' &&
                            Object.entries(aggregatedGroups).map(([cc, items]) => (
                                <AggregatedCostCenterTable
                                    key={cc}
                                    costcenter={cc}
                                    items={items}
                                />
                            ))
                        }
                    </>
                )}
            </div>

            <Footer />
        </div>
    );
};

export default WastageReport;